import React, { useEffect, useState, useContext } from "react";
import moment from "moment/moment";
import "moment/locale/th";
import { useNavigate } from "react-router";
import * as adminApi from "../../../../service/adminApi";
import { CurrentUserContext } from "../../../../App";
import PageHeaderV2 from "../../../pageheaderV2/PageHeaderV2";
import { RedirectCode400 } from "../../../RedirectCode400";
import TableSearchV1 from "../../../tableV1/tableSearchV1";
import format from "date-fns/format";
import { FaEye, FaEdit, FaTrash, FaCircle } from "react-icons/fa";

function PharmacogeneticsList() {
  const { setIsMessage } = useContext(CurrentUserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [guidelineList, setGuidelineList] = useState([]);
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [totalGuideline, setTotalGuideline] = useState(null);
  const [announcementListStorage, setAnnouncementListStorage] = useState([]);
  const [sortBy, setSortBy] = useState({ id: "", desc: false });

  const [filters, setFilters] = useState({
    title: "",
    "recorder.fname": "",
    "recorder.lname": "",
    "create_date.date": "",
    active: "",
    "update_date.date": "",
  });

  useEffect(() => {
    fetchGuidelines();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [page, filters, sortBy]);

  const fetchGuidelines = async () => {
    setLoading(true);
    let res = await adminApi.getPharmacogeneticsList(
      page,
      10,
      filters.name,
      filters.weight,
      filters.active,
      formatDate(filters["create_date.date"]),
      formatDate(filters["update_date.date"]),
      sortBy.id,
      sortBy.id ? (sortBy.desc ? "desc" : "asc") : "",
    );
    setLoading(false);

    if (res) {
      if (res["data"]["code"] === 200) {
        setGuidelineList(res["data"]["form_survey_list"]);
        setAnnouncementListStorage(res["data"]["form_survey_list"]);
        setMaxPage(Math.ceil(res["data"]["amount"] / 10));
        setTotalGuideline(res["data"]["amount"]);
      } else if (res["data"]["code"] === 403) {
        navigate(RedirectCode400());
      } else {
        setIsMessage({
          type: "warning",
          data: res["data"]["message_th"] || res["data"]["message"],
        });
      }
    } else {
      setIsMessage({
        type: "error",
        data: "เกิดปัญหาการใช้งาน กรุณาติดต่อเจ้าหน้าที่",
      });
    }
  };

  const removePharmacogenetics = async (item) => {
    if (!window.confirm(`คุณต้องการลบแบบฟอร์มส่งตรวจนี้ใช่หรือไม่`)) {
      return;
    }
    setLoading(true);
    let res = await adminApi.removePharmacogenetics(item["form_id"]);
    setLoading(false);
    if (res) {
      if (res["data"]["code"] === 200) {
        fetchGuidelines();
      } else if (res["data"]["code"] === 403) {
        navigate(RedirectCode400());
      } else {
        setIsMessage({
          type: "warning",
          data: res["data"]["message_th"] || res["data"]["message"],
        });
      }
    } else {
      setIsMessage({
        type: "error",
        data: "เกิดปัญหาการใช้งาน กรุณาติดต่อเจ้าหน้าที่",
      });
      setGuidelineList([]);
      setAnnouncementListStorage([]);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return format(date, "yyyy-MM-dd");
  };

  const optionStatusList = [
    {
      value: true,
      name: "Active",
    },
    {
      value: false,
      name: "InActive",
    },
  ];

  const formatDateThai = (date) => {
    const monthsThai = [
      "ม.ค.",
      "ก.พ.",
      "มี.ค.",
      "เม.ย.",
      "พ.ค.",
      "มิ.ย.",
      "ก.ค.",
      "ส.ค.",
      "ก.ย.",
      "ต.ค.",
      "พ.ย.",
      "ธ.ค.",
    ];
    const [year, month, day] = date.split("-");
    const formattedDate = `${parseInt(day)} ${
      monthsThai[parseInt(month) - 1]
    } ${year}`;
    return formattedDate;
  };

  const handleSortChange = (id, desc) => {
    setPage(1);
    setSortBy({ id, desc });
  };

  const columns = [
    {
      Header: "No.",
      accessor: "id",
      Filter: "#",
    },
    {
      Header: "ชื่อแบบฟอร์มส่งตรวจ",
      accessor: "name",
      Filter: "text",
    },
    {
      Header: "ลำดับ",
      accessor: "weight",
      Filter: "text",
    },
    {
      Header: "สถานะ",
      accessor: "active",
      status: true,
      DataFilter: optionStatusList,
      Filter: "select",
      Cell: (row) => (
        <>
          {row.row.original.active ? (
            <div className="text-[#64BD86] w-[100px] flex flex-row justify-center items-center gap-[5px]">
              <FaCircle /> Active
            </div>
          ) : (
            <div className="text-[#BEBEBE] w-[100px] flex flex-row justify-center items-center gap-[5px]">
              <FaCircle /> Inactive
            </div>
          )}
        </>
      ),
    },
    {
      Header: "วันที่สร้าง",
      accessor: "create_date.date",
      Cell: ({ row }) => {
        const createdDate =
          row.original.create_date && row.original.create_date.date
            ? row.original.create_date.date
            : null;
        return createdDate ? formatDateThai(createdDate) : "";
      },
      Filter: "date",
    },
    {
      Header: "วันที่แก้ไข",
      accessor: "update_date.date",
      Cell: ({ row }) => {
        const updatedDate =
          row.original.update_date && row.original.update_date.date
            ? row.original.update_date.date
            : null;
        return updatedDate ? formatDateThai(updatedDate) : "-";
      },
      Filter: "date",
    },
    {
      Header: "#",
      disableSortBy: true,
      accessor: "actions",
      Cell: ({ row }) => {
        const i = row.original;

        return (
          <div className="flex flex-row justify-center items-center gap-[6px]">
            <div
              className="flex justify-center items-center h-[30px] p-2 rounded-[5px] cursor-pointer"
              style={{ background: "#DFEEFA 0% 0% no-repeat padding-box" }}
              onClick={() => {
                navigate(
                  `/admin/management/pharmacogenetics/view-pharmacogenetics`,
                  {
                    state: {
                      type: "view-pharmacogenetics",
                      id: i["form_id"],
                      name: i["name"],
                      active: i["active"],
                      weight: i["weight"],
                      image: i["url"],
                      fname: i["created_by"]["fname"],
                      lname: i["created_by"]["lname"],
                      create_date: i["create_date"]["date"],
                      update_date: i["update_date"]
                        ? i["update_date"]["date"]
                        : i["create_date"]["date"],
                    },
                  },
                );
              }}
            >
              <FaEye className="text-[#46A8F4] h-[14px]" />
            </div>
            <div
              className="flex justify-center items-center h-[30px] p-2 rounded-[5px] cursor-pointer"
              style={{ background: "#AFEBE3 0% 0% no-repeat padding-box" }}
              onClick={() => {
                navigate(
                  "/admin/management/pharmacogenetics/edit-pharmacogenetics",
                  {
                    state: {
                      type: "edit-pharmacogenetics",
                      id: i["form_id"],
                      name: i["name"],
                      active: i["active"],
                      weight: i["weight"],
                      image: i["url"],
                    },
                  },
                );
              }}
            >
              <FaEdit className="text-[#00917E] h-[14px]" />
            </div>
            <button
              className="flex justify-center items-center h-[30px] p-2 rounded-[5px] cursor-pointer"
              style={{ background: "#FCD8DB 0% 0% no-repeat padding-box" }}
              onClick={() => removePharmacogenetics(i)}
            >
              <FaTrash className="text-[#D32736] h-[14px]" />
            </button>
          </div>
        );
      },
    },
  ];
  return (
    <div className="min-w-[708px] overflow-x-auto">
      <div className="mt-[60px]"></div>
      <div>
        <PageHeaderV2 titlename={"แบบฟอร์มส่งตรวจ"} />
        <TableSearchV1
          columns={columns}
          data={guidelineList}
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          total={totalGuideline}
          page={page}
          maxPage={maxPage}
          setPage={setPage}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          addData={{
            name: "เพิ่มแบบฟอร์มส่งตรวจ",
            url: "/admin/management/pharmacogenetics/add-pharmacogenetics",
            type: "pharmacogenetics",
          }}
          noDataComponent={<div className="w-full text-left">ไม่พบข้อมูล</div>}
        />
      </div>
    </div>
  );
}

export default PharmacogeneticsList;
